/**
 * Platform, Create Store (BLU-1376): one page that leaves a new client ready
 * before they first sign in. Three cards, in this order: the store; its
 * Shopify app (domain, client ID, client secret, install link; all four or
 * none); the store's admin (email, optional names). No password and no access
 * token: the server creates the store, registers the app and saves the link
 * (nothing is sent to Shopify), then LAST invites the admin, so their first
 * sign-in shows Install on Shopify.
 *
 * After the store exists the server never throws: a later failure comes back
 * as `partial` and the page lands on the store page with the failure in
 * router state, where the Left to do card shows the reason and what is left.
 */
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { trpc } from '@/lib/trpc';
import { useToast } from '@/hooks/use-toast';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { noAutofillProps } from '@/components/ui/no-autofill';
import { SecretInput } from '@/components/ui/secret-input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { isShopifyInstallLink } from '@/features/organization/components/shopify-connection/presentation';
import { SetupValues } from '@/features/organization/components/shopify-connection/SetupValues';

/**
 * Normalize various Shopify domain/URL formats to { handle, domain }.
 * Accepts:
 *   - admin.shopify.com/store/my-brand
 *   - https://admin.shopify.com/store/my-brand
 *   - my-brand.myshopify.com
 *   - https://my-brand.myshopify.com
 *   - plain handle: my-brand
 */
function normalizeShopifyDomain(input: string): { handle: string; domain: string } | null {
  const trimmed = input.trim();
  if (!trimmed) return null;

  // Strip protocol
  const withoutProtocol = trimmed.replace(/^https?:\/\//, '');
  // Remove trailing slashes/paths beyond what we need
  const cleaned = withoutProtocol.replace(/\/+$/, '');

  // Pattern: admin.shopify.com/store/<handle>
  const adminMatch = cleaned.match(/^admin\.shopify\.com\/store\/([a-z0-9][\w-]*)/i);
  if (adminMatch?.[1]) {
    const handle = adminMatch[1].toLowerCase();
    return { handle, domain: `${handle}.myshopify.com` };
  }

  // Pattern: <handle>.myshopify.com
  const myshopifyMatch = cleaned.match(/^([a-z0-9][\w-]*)\.myshopify\.com/i);
  if (myshopifyMatch?.[1]) {
    const handle = myshopifyMatch[1].toLowerCase();
    return { handle, domain: `${handle}.myshopify.com` };
  }

  // Treat as plain handle if it looks like one (no dots, no slashes)
  if (/^[a-z0-9][\w-]*$/i.test(cleaned)) {
    const handle = cleaned.toLowerCase();
    return { handle, domain: `${handle}.myshopify.com` };
  }

  return null;
}

/** Mirrors the backend's exported CLIENT_ID_PATTERN (the register RPC's rule); the server re-checks. */
const CLIENT_ID_PATTERN = /^[a-f0-9]{32}$/;

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const INITIAL_FORM = {
  organizationName: '',
  slug: '',
  shopifyDomainInput: '',
  shopifyDomain: '',
  shopifyClientId: '',
  shopifyClientSecret: '',
  installLink: '',
  adminEmail: '',
  adminFirstName: '',
  adminLastName: '',
};

export default function CreateStore() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const utils = trpc.useUtils();
  const [form, setForm] = useState(INITIAL_FORM);
  const [domainPreview, setDomainPreview] = useState<string | null>(null);
  const [domainError, setDomainError] = useState(false);

  // Email check: informational only. An email that already has an account is
  // accepted; that person gets an org invite to accept.
  const [emailCheckValue, setEmailCheckValue] = useState('');

  const emailCheck = trpc.platform.checkEmailExists.useQuery(
    { email: emailCheckValue },
    { enabled: !!emailCheckValue && emailCheckValue.includes('@') },
  );

  // Debounce the email check
  useEffect(() => {
    if (!form.adminEmail || !form.adminEmail.includes('@')) {
      setEmailCheckValue('');
      return;
    }
    const timer = setTimeout(() => {
      setEmailCheckValue(form.adminEmail);
    }, 300);
    return () => clearTimeout(timer);
  }, [form.adminEmail]);

  const handleNameChange = useCallback((name: string) => {
    const slug = name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
    setForm(f => ({ ...f, organizationName: name, slug }));
  }, []);

  const handleDomainInputChange = useCallback((value: string) => {
    setForm(f => ({ ...f, shopifyDomainInput: value }));
    const result = normalizeShopifyDomain(value);
    if (result) {
      setDomainPreview(result.domain);
      setDomainError(false);
      setForm(f => ({ ...f, shopifyDomainInput: value, shopifyDomain: result.domain }));
    } else if (value.trim()) {
      setDomainPreview(null);
      setDomainError(true);
      setForm(f => ({ ...f, shopifyDomainInput: value, shopifyDomain: '' }));
    } else {
      setDomainPreview(null);
      setDomainError(false);
      setForm(f => ({ ...f, shopifyDomainInput: value, shopifyDomain: '' }));
    }
  }, []);

  const createStore = trpc.platform.createStore.useMutation({
    onSuccess: (result) => {
      void utils.platform.listOrganizations.invalidate();
      // The new org auto-joins every platform admin (BLU-1313 DB trigger), so
      // the org switcher's list just grew for the caller.
      void utils.organization.getOrgLocationOptions.invalidate();
      const storePage = `/platform/stores/${result.organizationId}`;
      if (result.outcome === 'partial') {
        toast({ title: 'Store created, but setup is not finished' });
        navigate(storePage, {
          state: {
            createStoreFailure: { failedStep: result.failedStep, code: result.code, detail: result.detail },
          },
        });
        return;
      }
      toast({ title: 'Store created' });
      navigate(storePage);
    },
    onError: (error) => {
      // Nothing was written: stay on the form.
      toast({
        title: 'Error',
        description: error.message || 'Failed to create store',
        variant: 'destructive',
      });
    },
  });

  const isLoading = createStore.isPending;
  const existingAccount = emailCheck.data?.exists === true && emailCheckValue === form.adminEmail;

  // Shopify: all four fields or none.
  const clientId = form.shopifyClientId.trim().toLowerCase();
  const clientSecret = form.shopifyClientSecret.trim();
  const installLink = form.installLink.trim();
  const shopifyFilled = [form.shopifyDomainInput.trim(), clientId, clientSecret, installLink].filter(Boolean).length;
  const clientIdValid = CLIENT_ID_PATTERN.test(clientId);
  const clientIdError = clientId !== '' && !clientIdValid;
  const installLinkError = installLink !== '' && !isShopifyInstallLink(installLink);
  const shopifyPartial = shopifyFilled > 0 && shopifyFilled < 4;
  const shopifyComplete =
    shopifyFilled === 4 && Boolean(form.shopifyDomain) && !clientIdError && !installLinkError;
  const shopifyOk = shopifyFilled === 0 || shopifyComplete;

  // The Dev Dashboard values for this client id, so the install link can be
  // made before submitting. Pure on the server (no DB, no Shopify); at most one
  // call per distinct valid id per 5 minutes.
  const setupValues = trpc.platform.getShopifyAppSetupValues.useQuery(
    { clientId },
    { enabled: clientIdValid, staleTime: 5 * 60_000 },
  );

  const adminFirstName = form.adminFirstName.trim();
  const adminLastName = form.adminLastName.trim();

  const canSubmit =
    form.organizationName.trim() !== '' &&
    form.slug !== '' &&
    EMAIL_PATTERN.test(form.adminEmail.trim()) &&
    shopifyOk &&
    !isLoading;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    createStore.mutate({
      organizationName: form.organizationName,
      slug: form.slug,
      adminEmail: form.adminEmail.trim(),
      // Blank names are omitted; the invite then carries none.
      ...(adminFirstName ? { adminFirstName } : {}),
      ...(adminLastName ? { adminLastName } : {}),
      ...(shopifyComplete
        ? { shopify: { shopDomain: form.shopifyDomain, clientId, clientSecret, installLink } }
        : {}),
    });
  };

  return (
    <div className="flex flex-col">
      <main className="flex-1 w-full py-6 px-4 sm:px-6 lg:px-8 max-w-3xl">
        <Button variant="ghost" size="sm" className="mb-4" onClick={() => navigate('/platform/stores')}>
          <ArrowLeft className="mr-2 h-4 w-4" /> Back to Stores
        </Button>

        <div className="mb-6">
          <h2 className="text-2xl font-bold tracking-tight">Add Store</h2>
          <p className="text-muted-foreground text-sm mt-0.5">
            Create the store, register its Shopify app and invite its admin, before the client signs in.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6" autoComplete="off" aria-label="Create store">
          {/* 1. Store */}
          <Card>
            <CardHeader className="pb-4">
              <CardTitle className="text-lg">Store Details</CardTitle>
              <CardDescription>The store name and URL slug used in this platform.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="storeName">Store Name</Label>
                <Input
                  id="storeName"
                  value={form.organizationName}
                  onChange={e => handleNameChange(e.target.value)}
                  required
                  disabled={isLoading}
                  placeholder="My Brand"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="slug">Slug</Label>
                <Input
                  id="slug"
                  value={form.slug}
                  onChange={e => setForm(f => ({ ...f, slug: e.target.value }))}
                  required
                  disabled={isLoading}
                  placeholder="my-brand"
                />
                <p className="text-xs text-muted-foreground">
                  Used in URLs. Auto-generated from the store name.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* 2. Shopify: all four fields or none. Nothing is sent to Shopify. */}
          <Card>
            <CardHeader className="pb-4">
              <CardTitle className="text-lg">
                Shopify{' '}
                <span className="text-muted-foreground font-normal text-sm">(optional)</span>
              </CardTitle>
              <CardDescription>
                Register the store's Shopify app and paste its install link. Nothing is sent to Shopify now:
                the store connects when the client clicks Install on Shopify.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="shopifyDomain">Shopify Domain</Label>
                <Input
                  id="shopifyDomain"
                  value={form.shopifyDomainInput}
                  onChange={e => handleDomainInputChange(e.target.value)}
                  disabled={isLoading}
                  placeholder="my-brand.myshopify.com"
                  className={domainError ? 'border-destructive' : ''}
                />
                <p className="text-xs text-muted-foreground">
                  Paste your Shopify URL or enter your store handle. Both formats accepted.
                </p>
                {domainPreview && (
                  <p className="text-xs text-green-600 font-medium">
                    {domainPreview}
                  </p>
                )}
                {domainError && (
                  <p className="text-xs text-destructive">
                    Couldn't parse domain. Try "my-brand.myshopify.com" or "admin.shopify.com/store/my-brand".
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="clientId">Client ID</Label>
                <Input
                  id="clientId"
                  value={form.shopifyClientId}
                  onChange={e => setForm(f => ({ ...f, shopifyClientId: e.target.value }))}
                  disabled={isLoading}
                  aria-invalid={clientIdError}
                  className={clientIdError ? 'border-destructive' : ''}
                  {...noAutofillProps}
                />
                {clientIdError ? (
                  <p className="text-xs text-destructive">Client ID is 32 letters and numbers from the Dev Dashboard.</p>
                ) : (
                  <p className="text-xs text-muted-foreground">From the store's app in the Shopify Dev Dashboard.</p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="clientSecret">Client Secret</Label>
                <SecretInput
                  id="clientSecret"
                  value={form.shopifyClientSecret}
                  onChange={e => setForm(f => ({ ...f, shopifyClientSecret: e.target.value }))}
                  disabled={isLoading}
                />
                <p className="text-xs text-muted-foreground">
                  Stored securely for the install. It is never shown again.
                </p>
              </div>
              {clientIdValid && (
                <div className="space-y-2">
                  <p className="text-sm font-medium">Values for the Shopify app</p>
                  {setupValues.data ? (
                    <SetupValues setup={setupValues.data} />
                  ) : setupValues.isLoading ? (
                    <p className="text-xs text-muted-foreground">Loading the app values</p>
                  ) : null}
                </div>
              )}
              <div className="space-y-2">
                <Label htmlFor="installLink">Install link</Label>
                <Input
                  id="installLink"
                  value={form.installLink}
                  onChange={e => setForm(f => ({ ...f, installLink: e.target.value }))}
                  disabled={isLoading}
                  aria-invalid={installLinkError}
                  className={installLinkError ? 'border-destructive' : ''}
                  {...noAutofillProps}
                />
                {installLinkError ? (
                  <p className="text-xs text-destructive">
                    Paste the install link from the Shopify Dev Dashboard. It starts with https://admin.shopify.com/.
                  </p>
                ) : (
                  <p className="text-xs text-muted-foreground">
                    The client sees an Install on Shopify button with this link when they sign in.
                  </p>
                )}
              </div>
              {shopifyPartial && (
                <p className="text-xs text-amber-700">
                  Fill in all four Shopify fields, or leave them all blank to set up Shopify later on the store page.
                </p>
              )}
            </CardContent>
          </Card>

          {/* 3. Store admin: invited last. */}
          <Card>
            <CardHeader className="pb-4">
              <CardTitle className="text-lg">Store admin</CardTitle>
              <CardDescription>
                We send the invite last, after the store and its Shopify link are saved, so their first sign-in
                shows the Install on Shopify button.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="adminEmail">Email</Label>
                <div className="relative">
                  <Input
                    id="adminEmail"
                    type="email"
                    value={form.adminEmail}
                    onChange={e => setForm(f => ({ ...f, adminEmail: e.target.value }))}
                    required
                    disabled={isLoading}
                    {...noAutofillProps}
                  />
                  {emailCheck.isFetching && (
                    <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-muted-foreground" />
                  )}
                </div>
                {existingAccount && (
                  <p className="text-xs text-muted-foreground">
                    This person already has an account. They will get an invite to accept.
                  </p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="firstName">First Name</Label>
                  <Input
                    id="firstName"
                    value={form.adminFirstName}
                    onChange={e => setForm(f => ({ ...f, adminFirstName: e.target.value }))}
                    disabled={isLoading}
                  />
                  <p className="text-xs text-muted-foreground">Optional.</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="lastName">Last Name</Label>
                  <Input
                    id="lastName"
                    value={form.adminLastName}
                    onChange={e => setForm(f => ({ ...f, adminLastName: e.target.value }))}
                    disabled={isLoading}
                  />
                  <p className="text-xs text-muted-foreground">Optional.</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Submit */}
          <div className="flex justify-end gap-3 pb-6">
            <Button type="button" variant="outline" onClick={() => navigate('/platform/stores')}>
              Cancel
            </Button>
            <Button type="submit" disabled={!canSubmit}>
              {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {isLoading ? 'Creating...' : 'Create Store'}
            </Button>
          </div>
        </form>
      </main>
    </div>
  );
}
